import type {
  ChatMessage,
  ChatMessageModelImagePart,
  ChatMessagePart,
  ChatMessageTextPart,
  MessagesResponse,
  ReattachedModelImage,
  RequestConfig,
  RequestInstruction,
  RequestPreferences,
  SurveyPicks,
  UserMessage,
} from '@Components/ai-chat-window/ai-chat-window.types';
import {ChatMessagePartType, MessageRole, NEXT_SUGGESTIONS_MESSAGE_ID, TextPartPurpose} from '@Components/ai-chat-window/ai-chat-window.types';
import {replaceThinkingOrAppendMessage} from '@Components/ai-chat-window/lib/ai-chat-stream.library';
import {createComponentPart} from '@Components/ai-chat-window/lib/stream-component-registry';
import {StreamComponentType} from '@Components/ai-chat-window/lib/stream-component-registry.types';
import type {AiPromptBoxAttachments} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';

/**
 * Builds a user message that replaces the content of an existing message (e.g. after edit).
 */
export const buildUserMessageReplacement = (messageId: string, parts: ChatMessagePart[], requestPreferences?: RequestPreferences): UserMessage => ({
  role: MessageRole.USER,
  parts,
  messageId,
  requestPreferences,
});

function pruneParts(messages: Record<string, ChatMessage>, keepPart: (part: ChatMessagePart) => boolean, keepMessage?: (message: ChatMessage) => boolean): Record<string, ChatMessage> {
  const pruned: [string, ChatMessage][] = [];

  for (const [key, message] of Object.entries(messages)) {
    if (keepMessage && !keepMessage(message)) {
      continue;
    }

    const parts = message.parts ?? [];
    const kept = parts.filter(keepPart);

    if (parts.length > 0 && kept.length === 0) {
      continue;
    }

    if (kept.length === parts.length) {
      pruned.push([key, message]);
      continue;
    }

    pruned.push([key, {...message, parts: kept}]);
  }

  return Object.fromEntries(pruned);
}

const isNextSuggestion = (part: ChatMessagePart): boolean => 'isNextSuggestion' in part && Boolean(part.isNextSuggestion);

const isPendingPlaceholder = (part: ChatMessagePart): boolean =>
  part.type === ChatMessagePartType.MODEL_GENERATED_IMAGE && Boolean(part.isSkeletonPlaceholder);

const isSettledMessage = (message: ChatMessage): boolean => !(message.role === MessageRole.ASSISTANT && message.isThinking);

export function stripNextSuggestionParts(messages: Record<string, ChatMessage>): Record<string, ChatMessage> {
  return pruneParts(messages, (part) => !isNextSuggestion(part));
}

export function stripPendingPlaceholders(messages: Record<string, ChatMessage>): Record<string, ChatMessage> {
  return pruneParts(messages, (part) => !isPendingPlaceholder(part), isSettledMessage);
}

export function dropThinkingMessages(messages: Record<string, ChatMessage>): Record<string, ChatMessage> {
  return Object.fromEntries(Object.entries(messages).filter(([, message]) => isSettledMessage(message)));
}

export function withNextSuggestions(messages: Record<string, ChatMessage>, props: Record<string, unknown> | undefined): Record<string, ChatMessage> {
  const cleaned = stripNextSuggestionParts(messages);

  if (!props) {
    return cleaned;
  }

  const componentPart = createComponentPart({componentType: StreamComponentType.BUTTON_GROUP, props});

  if (!componentPart) {
    return cleaned;
  }

  const suggestionParts: ChatMessagePart[] = [{...componentPart, isNextSuggestion: true}];

  return replaceThinkingOrAppendMessage(cleaned, {
    role: MessageRole.ASSISTANT,
    isThinking: false,
    parts: suggestionParts,
    messageId: NEXT_SUGGESTIONS_MESSAGE_ID,
    feedback: null,
    usage: {inputTokens: 0, outputTokens: 0},
  });
}

export const convertMessagesArrayToHashmap = (messages: ChatMessage[]): Record<string, ChatMessage> => {
  if (!messages || messages.length === 0) {
    return {};
  }

  return messages.reduce<Record<string, ChatMessage>>((acc, message, index) => {
    const messageId = message.messageId ?? index.toString();

    acc[messageId] = message;

    return acc;
  }, {});
};

export const getAiChatCompletedStateForInProgressChat = async (chatId: string, messageSessionUid: string): Promise<MessagesResponse> => {
  return (await window.PMW.pollingAjaxCallAsync(
    (): Promise<void> => Promise.resolve(),
    async (): Promise<MessagesResponse> => {
      return window.PMW.readLocal('ai/getAiChatCompletedState', {
        chatId: chatId,
        messageSessionUid: messageSessionUid,
      });
    }
  )) as MessagesResponse;
};

const shouldIncludeMessage = (message: ChatMessage): boolean => {
  if (message.role === MessageRole.DEVELOPER) {
    return 'failureType' in message;
  }

  return true;
};

export const groupMessagesIntoConversations = (messages: Record<string, ChatMessage>): ChatMessage[][] => {
  const conversations: ChatMessage[][] = [];
  let currentConversation: ChatMessage[] = [];

  const orderedMessages = Object.values(messages).filter(shouldIncludeMessage);

  orderedMessages.forEach((message) => {
    if (message.role === MessageRole.USER) {
      if (currentConversation.length > 0) {
        conversations.push(currentConversation);
      }
      currentConversation = [message];
    } else {
      currentConversation.push(message);
    }
  });

  if (currentConversation.length > 0) {
    conversations.push(currentConversation);
  }

  return conversations;
};

export const findLastUserMessage = (messages: Record<string, ChatMessage>): ChatMessage | undefined => {
  const messageValues = Object.values(messages);

  return messageValues.reverse().find((msg) => msg.role === MessageRole.USER);
};

export const isFinalGeneratedImagePart = (part: ChatMessageModelImagePart): boolean => {
  return part.hashedId !== '' && !part.isRawBlurred && !part.isSkeletonPlaceholder;
};

export const findLatestModelImagePart = (messages: Record<string, ChatMessage>): {messageId: string; part: ChatMessageModelImagePart} | null => {
  const entries = Object.entries(messages).reverse();

  for (const [messageId, message] of entries) {
    if (message.role !== MessageRole.ASSISTANT) {
      continue;
    }

    const imageParts = (message.parts ?? []).filter((part): part is ChatMessageModelImagePart => part.type === ChatMessagePartType.MODEL_GENERATED_IMAGE);
    const latestPart = imageParts[imageParts.length - 1];

    if (latestPart) {
      return {messageId, part: latestPart};
    }
  }

  return null;
};

export const buildRequestPreferences = (pinnedAspectRatio: string | null, pinnedBrandKitId: string | null): RequestPreferences | undefined => {
  const preferences: RequestPreferences = {};

  if (pinnedAspectRatio) {
    preferences.aspectRatio = pinnedAspectRatio;
  }

  if (pinnedBrandKitId) {
    preferences.brandKitId = pinnedBrandKitId;
  }

  return Object.keys(preferences).length > 0 ? preferences : undefined;
};

interface BuildRequestConfigOptions {
  existingRequestConfig?: RequestConfig;
  requestInstruction?: RequestInstruction;
  preferences?: RequestPreferences;
  surveyPicks?: SurveyPicks;
}

export const buildRequestConfig = ({existingRequestConfig, requestInstruction, preferences, surveyPicks}: BuildRequestConfigOptions): RequestConfig | undefined => {
  let config = existingRequestConfig;

  if (preferences) {
    config = {...config, preferences: {...config?.preferences, ...preferences}};
  }

  if (requestInstruction) {
    config = {...config, requestInstruction};
  }

  if (surveyPicks) {
    config = {...config, surveyPicks};
  }

  return config;
};

export const convertAttachmentsToUserRequestParts = (attachments: AiPromptBoxAttachments['items']): ChatMessagePart[] => {
  const userRequestParts: ChatMessagePart[] = [];

  attachments.forEach((attachment) => {
    switch (attachment.type) {
      case PromptAttachmentType.IMAGE:
      case PromptAttachmentType.CAPTURE_PHOTO:
        userRequestParts.push({
          type: ChatMessagePartType.USER_IMAGE,
          hashedFilename: attachment.hashedFilename,
          source: attachment.source,
          extension: attachment.extension,
        });
        break;
      case PromptAttachmentType.DESIGN:
      case PromptAttachmentType.DESIGN_TEMPLATE:
        userRequestParts.push({
          type: ChatMessagePartType.DESIGN,
          hashedId: attachment.designId,
          pageHashedId: attachment.pageHashedId,
        });
        break;
      case PromptAttachmentType.EXAMPLE_IMAGE:
        userRequestParts.push({
          type: ChatMessagePartType.EXAMPLE_IMAGE_ASSET,
          fileName: attachment.fileName,
        });
        break;
      case PromptAttachmentType.REATTACHED_MODEL_IMAGE:
        userRequestParts.push({
          type: ChatMessagePartType.REATTACHED_MODEL_IMAGE,
          hashedId: attachment.imageHashedId,
        });
        break;
    }
  });

  return userRequestParts;
};

export const createUserRequestParts = (prompt: string, attachments: AiPromptBoxAttachments['items'], purpose?: TextPartPurpose): ChatMessagePart[] => {
  const attachmentsUserRequestParts = convertAttachmentsToUserRequestParts(attachments);
  const textPart: ChatMessageTextPart = {type: ChatMessagePartType.TEXT, text: prompt};
  if (purpose) {
    textPart.purpose = purpose;
  }
  return [textPart, ...attachmentsUserRequestParts];
};

export const messagesAfter = (messages: Record<string, ChatMessage>, messageId: string): ChatMessage[] => {
  const orderedMessages = Object.values(messages);
  const messagePosition = orderedMessages.findIndex((message) => message.messageId === messageId);

  return messagePosition === -1 ? [] : orderedMessages.slice(messagePosition + 1);
};

export const findPrecedingMessageIndex = (orderedMessages: ChatMessage[], role: MessageRole, beforeIndex: number): number => {
  for (let index = beforeIndex - 1; index >= 0; index--) {
    if (orderedMessages[index].role === role) {
      return index;
    }
  }

  return -1;
};

const NEXT_SUGGESTION_AGENT = 'marketing-next-suggestion-agent';

const isTurnClosingMessage = (message: ChatMessage): boolean => {
  if (message.messageId === NEXT_SUGGESTIONS_MESSAGE_ID) {
    return true;
  }

  if (message.role !== MessageRole.ASSISTANT) {
    return false;
  }

  if (message.producedByAgent === NEXT_SUGGESTION_AGENT) {
    return true;
  }

  const parts = message.parts ?? [];

  return parts.length > 0 && parts.every((part) => part.type === ChatMessagePartType.COMPONENT);
};

export const lastInteractiveMessageIndex = (messages: ChatMessage[]): number => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (!isTurnClosingMessage(messages[index])) {
      return index;
    }
  }

  return -1;
};

interface ModelImagePartLocation {
  part: ChatMessageModelImagePart;
  messageIndex: number;
}

/**
 * Returns null when the given image is not itself the product of a change-style turn.
 */
export const getChangeStyleChainOriginalImage = (messages: Record<string, ChatMessage> | undefined, currentImageHashedId: string): ChatMessageModelImagePart | null => {
  if (!messages) {
    return null;
  }

  const orderedMessages = Object.values(messages);
  let cursor = findModelImagePart(orderedMessages, currentImageHashedId, orderedMessages.length - 1);
  let originalImagePart: ChatMessageModelImagePart | null = null;

  while (cursor) {
    const triggerIndex = findPrecedingMessageIndex(orderedMessages, MessageRole.USER, cursor.messageIndex);
    if (triggerIndex === -1) {
      break;
    }

    const restyledSourceHashedId = getChangeStyleReattachedHashedId(orderedMessages[triggerIndex]);
    if (!restyledSourceHashedId) {
      break;
    }

    cursor = findModelImagePart(orderedMessages, restyledSourceHashedId, triggerIndex - 1);
    if (cursor) {
      originalImagePart = cursor.part;
    }
  }

  return originalImagePart;
};

const findModelImagePart = (orderedMessages: ChatMessage[], hashedId: string, fromIndex: number): ModelImagePartLocation | null => {
  for (let index = Math.min(fromIndex, orderedMessages.length - 1); index >= 0; index--) {
    const part = getModelImagePartByHashedId(orderedMessages[index], hashedId);
    if (part) {
      return {part, messageIndex: index};
    }
  }
  return null;
};

const getChangeStyleReattachedHashedId = (message: ChatMessage): string | null => {
  const isChangeStyleTrigger = message.parts.some((part): part is ChatMessageTextPart => part.type === ChatMessagePartType.TEXT && part.purpose === TextPartPurpose.CHANGE_STYLE);
  if (!isChangeStyleTrigger) {
    return null;
  }

  const reattachedPart = message.parts.find((part): part is ReattachedModelImage => part.type === ChatMessagePartType.REATTACHED_MODEL_IMAGE);
  return reattachedPart?.hashedId ?? null;
};

const getModelImagePartByHashedId = (message: ChatMessage, hashedId: string): ChatMessageModelImagePart | null => {
  if (message.role !== MessageRole.ASSISTANT) {
    return null;
  }
  return message.parts.find((part): part is ChatMessageModelImagePart => part.type === ChatMessagePartType.MODEL_GENERATED_IMAGE && part.hashedId === hashedId) ?? null;
};
